import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Settings,
  CheckCircle, Circle, ChevronRight, ChevronDown, 
  ArrowLeft, Clock, BookOpen, Star, Share2, 
  Download, MessageSquare, Info, Award, Loader2,
  RotateCcw, Sparkles
} from 'lucide-react';
import CourseHeader from '../../components/CourseHeader';
import { useNavigate, useLocation } from 'react-router-dom';
import { postRequest, getRequest } from '../../utils/networking';
import { apiUrls, videoUrl as videoBaseUrl, baseUrl } from '../../utils/url';
import { toast } from 'react-toastify';

const PlaybackSpeedIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M10 8L16 12L10 16V8Z" fill="currentColor"/>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4"/>
  </svg>
);

const parseDurationToSeconds = (duration) => {
  if (!duration) return 0;
  if (typeof duration === 'number') return Math.floor(duration);
  const str = String(duration).trim();
  if (!isNaN(str)) return Math.floor(Number(str));
  const parts = str.split(':').map(p => parseInt(p, 10));
  if (parts.some(isNaN)) return 0;
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  } else if (parts.length === 1) {
    return parts[0];
  }
  return 0;
};

const formatTime = (timeInSeconds) => {
  if (isNaN(timeInSeconds) || timeInSeconds === null || timeInSeconds === undefined || timeInSeconds < 0) return '0:00';
  const total = Math.floor(timeInSeconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) {
    return `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

const getVideoNumericId = (video) => {
  if (!video) return null;
  const raw = video.videoid ?? video.curriculamid ?? video.id;
  const num = Number(raw);
  return !isNaN(num) && num > 0 ? num : raw;
};

const CourseDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const courseId = location.state?.id;
  const videoRef = useRef(null);
  const lastSaveTimeRef = useRef(0);
  const lastLoadedVideoIdRef = useRef(null);
  const selectedVideoRef = useRef(null);

  const [topics, setTopics] = useState([]);
  const [courseInfo, setCourseInfo] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [expandedSyllabus, setExpandedSyllabus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Video player states
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [durationStr, setDurationStr] = useState('0:00');
  const [currentTimeStr, setCurrentTimeStr] = useState('0:00');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [resumeBanner, setResumeBanner] = useState(null);
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  
  const controlsTimeoutRef = useRef(null);
  const playerContainerRef = useRef(null);

  useEffect(() => {
    selectedVideoRef.current = selectedVideo;
  }, [selectedVideo]);

  // Send video progress to backend (throttled)
  const sendProgress = useCallback(async (current, duration, force = false) => {
    const currentVideo = selectedVideoRef.current;
    if (!currentVideo) return;
    const videoId = getVideoNumericId(currentVideo);
    if (!videoId) return;

    const currentSec = Math.floor(current || 0);
    const durationSec = Math.floor(duration || parseDurationToSeconds(currentVideo.duration) || 0);

    const now = Date.now();
    if (!force && now - lastSaveTimeRef.current < 5000) {
      return; // throttled to every 5s
    }
    lastSaveTimeRef.current = now;

    try {
      const payload = {
        videoid: typeof videoId === 'number' ? videoId : Number(videoId) || videoId,
        watched_seconds: currentSec,
        duration_seconds: durationSec
      };
      
      await postRequest(apiUrls.saveprogress, payload);

      const isCompleted = durationSec > 0 && currentSec >= durationSec * 0.95;
      const progressPercent = durationSec > 0 ? Math.min(100, Math.round((currentSec / durationSec) * 100)) : 0;

      // Update topics progress bar smoothly
      setTopics(prev => prev.map(topic => ({
        ...topic,
        items: (topic.items || []).map(item => {
          const itemId = getVideoNumericId(item);
          if (String(itemId) === String(videoId)) {
            return {
              ...item,
              watched_seconds: currentSec,
              duration_seconds: durationSec > 0 ? durationSec : item.duration_seconds,
              isCompleted: isCompleted || item.isCompleted,
              progressPercent: Math.max(item.progressPercent || 0, progressPercent)
            };
          }
          return item;
        })
      })));
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  }, []);

  // Reset progress (Watch Again)
  const handleResetProgress = async (videoToReset, e) => {
    if (e) e.stopPropagation();
    const targetVideo = videoToReset || selectedVideoRef.current;
    if (!targetVideo) return;
    const videoId = getVideoNumericId(targetVideo);
    if (!videoId) return;

    try {
      const resp = await postRequest(apiUrls.resetprogress, {
        videoid: typeof videoId === 'number' ? videoId : Number(videoId) || videoId
      });

      if (resp?.status === 'S' || resp?.status === 's') {
        toast.success("Progress reset. Starting from beginning.");
      }

      // Reset local state
      setTopics(prev => prev.map(topic => ({
        ...topic,
        items: (topic.items || []).map(item => {
          const itemId = getVideoNumericId(item);
          if (String(itemId) === String(videoId)) {
            return {
              ...item,
              watched_seconds: 0,
              isCompleted: false,
              progressPercent: 0
            };
          }
          return item;
        })
      })));

      if (selectedVideoRef.current && String(getVideoNumericId(selectedVideoRef.current)) === String(videoId)) {
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          setProgress(0);
          setCurrentTimeStr('0:00');
          videoRef.current.play().catch(() => {});
          setIsPlaying(true);
        }
        setIsVideoEnded(false);
        setResumeBanner(null);
      }
    } catch (err) {
      console.error('Error resetting progress:', err);
      toast.error("Failed to reset progress");
    }
  };

  const handleNextVideo = () => {
    let found = false;
    let next = null;
    for (const topic of topics) {
      for (const item of topic.items || []) {
        if (found) {
          next = item;
          break;
        }
        if (String(getVideoNumericId(item)) === String(getVideoNumericId(selectedVideo))) {
          found = true;
        }
      }
      if (next) break;
    }
    if (next) {
      handleVideoSelect(next);
    } else {
      toast.info("You have completed all lessons in this course!");
    }
  };

  useEffect(() => {
    if (!courseId) {
      navigate('/mycourse');
    }
  }, [courseId, navigate]);

  const getFullAssetUrl = (path) => {
    if (!path) return null;
    if (String(path).startsWith('http')) return path;
    
    const normalized = String(path).replace(/\\/g, "/");
    
    if (normalized.includes("ecomtamil/")) {
      const extractedPath = normalized.split("ecomtamil/").pop().replace(/^\/+/, '');
      return `${videoBaseUrl}/${extractedPath}`;
    }
    if (normalized.includes("assets/")) {
      const assetsIndex = normalized.indexOf("assets/");
      const extractedPath = normalized.substring(assetsIndex + 7).replace(/^\/+/, '');
      return `${videoBaseUrl}/${extractedPath}`;
    }
    
    const cleanPath = normalized.replace(/^\/+/, '');
    return `${videoBaseUrl}/coursevideos/${cleanPath}`;
  };

  const getAttachmentUrl = (v) => {
    const rawPath = v.fileurl || v.url || v.path || v.attachment_url || v.variant_url;
    if (!rawPath) return null;
    if (String(rawPath).startsWith('http')) return rawPath;
    
    const ext = (v.filetype || v.type || '').toLowerCase();
    const normalized = String(rawPath).replace(/\\/g, "/").replace(/^\/+/, '');
    const fileName = ext && !normalized.toLowerCase().endsWith(`.${ext}`) 
      ? `${normalized}.${ext}` 
      : normalized;
      
    if (normalized.includes("ecomtamil/")) {
      const extractedPath = normalized.split("ecomtamil/").pop().replace(/^\/+/, '');
      return `${videoBaseUrl}/${extractedPath}`;
    }
    if (normalized.includes("assets/")) {
      const assetsIndex = normalized.indexOf("assets/");
      const extractedPath = normalized.substring(assetsIndex + 7).replace(/^\/+/, '');
      return `${videoBaseUrl}/${extractedPath}`;
    }
    
    return `${videoBaseUrl}/coursevideos/${fileName}`;
  };

  // Initial Fetch: Course Info, Curriculum, and Progress Records
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 1. Fetch Course Info
        const ordersResponse = await postRequest(apiUrls.getmycourseorders);
        if (ordersResponse.status === "S" || ordersResponse.status === "s") {
          const myCourse = ordersResponse.data?.find(c => String(c.courseid) === String(courseId));
          if (myCourse) {
            setCourseInfo(myCourse);
          }
        }

        // 2. Fetch Curriculum
        const curriculumResponse = await postRequest(apiUrls.getcurriculams, { 
          courseid: courseId,
          id: courseId 
        });

        // 3. Fetch User Video Progress
        let progressData = [];
        try {
          const progResp = await postRequest(apiUrls.getprogress, {});
          if (progResp?.data && Array.isArray(progResp.data)) {
            progressData = progResp.data;
          } else if (Array.isArray(progResp)) {
            progressData = progResp;
          }
        } catch (error) {
          console.error("Error fetching progress:", error);
        }

        const progressMap = {};
        progressData.forEach(p => {
          if (p.videoid !== undefined && p.videoid !== null) {
            progressMap[Number(p.videoid)] = p;
            progressMap[String(p.videoid)] = p;
          }
          if (p.curriculamid) {
            progressMap[p.curriculamid] = p;
            progressMap[`${courseId}_${p.curriculamid}`] = p;
          }
        });

        let syllabusData = [];
        if (curriculumResponse.status === "S" && Array.isArray(curriculumResponse.data)) {
          syllabusData = curriculumResponse.data;
        } else if (Array.isArray(curriculumResponse)) {
          syllabusData = curriculumResponse;
        } else if (typeof curriculumResponse === 'object') {
          for (let key in curriculumResponse) {
            if (Array.isArray(curriculumResponse[key])) {
              syllabusData = curriculumResponse[key];
              break;
            }
          }
        }

        if (syllabusData.length > 0) {
          const isNewStructure = syllabusData.some(item => item.videos && Array.isArray(item.videos));
          
          let parsedTopics = [];
          if (isNewStructure) {
            parsedTopics = syllabusData.map((syllabusItem, idx) => {
               const videos = syllabusItem.videos || [];
               const mappedVideos = videos.map((video, vIdx) => {
                  const vidId = video.videoid || video.curriculamid || video.id || `${syllabusItem.id || idx}-${vIdx}`;
                  const numVidId = Number(vidId);
                  const pData = progressMap[numVidId] || progressMap[vidId] || progressMap[String(vidId)] || {};
                  
                  const watchedSeconds = Number(pData.watched_seconds || pData.last_position_seconds || pData.watchedTime || 0);
                  const durationSeconds = Number(pData.duration_seconds || parseDurationToSeconds(video.duration) || 0);
                  const isCompleted = pData.is_completed === true || pData.iscompleted === true || pData.iscompleted === "true" || (durationSeconds > 0 && watchedSeconds >= durationSeconds * 0.95);
                  const progressPercent = durationSeconds > 0 ? Math.min(100, Math.round((watchedSeconds / durationSeconds) * 100)) : (pData.progress_percent || 0);

                  return {
                    id: vidId,
                    videoid: vidId,
                    title: video.videotitle || video.title || `Video ${vIdx + 1}`,
                    duration: video.duration || formatTime(durationSeconds),
                    duration_seconds: durationSeconds,
                    watched_seconds: watchedSeconds,
                    videoUrl: getFullAssetUrl(video.videourl || video.url || video.video_url),
                    description: video.videodescription || video.description || '',
                    isCompleted: isCompleted,
                    progressPercent: progressPercent,
                    variants: video.variants || video.attachments || []
                  };
               });
               
               return {
                  id: syllabusItem.id || `syllabus-${idx}`,
                  title: syllabusItem.title || syllabusItem.module_title || `Module ${idx + 1}`,
                  items: mappedVideos,
                  variants: syllabusItem.variants || syllabusItem.attachments || [],
                  duration: syllabusItem.duration || '',
                  description: syllabusItem.description || ''
               };
            });
          } else {
            const mappedItems = syllabusData.map((item, index) => {
              const vidId = item.videoid || item.curriculamid || item.id || `item-${index}`;
              const numVidId = Number(vidId);
              const pData = progressMap[numVidId] || progressMap[vidId] || progressMap[String(vidId)] || {};

              const watchedSeconds = Number(pData.watched_seconds || pData.last_position_seconds || pData.watchedTime || 0);
              const durationSeconds = Number(pData.duration_seconds || parseDurationToSeconds(item.duration) || 0);
              const isCompleted = pData.is_completed === true || pData.iscompleted === true || pData.iscompleted === "true" || (durationSeconds > 0 && watchedSeconds >= durationSeconds * 0.95);
              const progressPercent = durationSeconds > 0 ? Math.min(100, Math.round((watchedSeconds / durationSeconds) * 100)) : (pData.progress_percent || 0);

              return {
                id: vidId,
                videoid: vidId,
                title: item.title || item.module_title || `Module ${index + 1}`,
                duration: item.duration || formatTime(durationSeconds),
                duration_seconds: durationSeconds,
                watched_seconds: watchedSeconds,
                videoUrl: getFullAssetUrl(item.url || item.video_url || item.videoUrl),
                isCompleted: isCompleted,
                progressPercent: progressPercent,
                description: item.description || '',
                variants: item.variants || item.attachments || []
              };
            });

            parsedTopics = [{
              id: 'main-section',
              title: 'Course Content',
              isExpanded: true,
              items: mappedItems,
              variants: []
            }];
          }

          setTopics(parsedTopics);
          if (parsedTopics.length > 0) {
            setExpandedSyllabus(parsedTopics[0].id);
            if (parsedTopics[0].items && parsedTopics[0].items.length > 0) {
              setSelectedVideo(parsedTopics[0].items[0]);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching course detail:", error);
        toast.error("Failed to load course content");
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      fetchData();
    }
  }, [courseId]);

  // YouTube-style Resume Playback ONLY when video ID actually changes
  useEffect(() => {
    const videoId = selectedVideo?.id || selectedVideo?.videoid;
    if (!selectedVideo?.videoUrl || !videoId) return;
    const videoEl = videoRef.current;
    if (!videoEl) return;

    // Prevent re-seeking while playing the same video
    if (lastLoadedVideoIdRef.current === String(videoId)) {
      return;
    }
    lastLoadedVideoIdRef.current = String(videoId);

    setIsVideoEnded(false);
    const watched = Number(selectedVideo.watched_seconds) || 0;
    const dur = Number(selectedVideo.duration_seconds) || parseDurationToSeconds(selectedVideo.duration) || 0;

    const isNearlyEnd = dur > 0 && watched >= dur - 3;
    if (watched > 0 && !selectedVideo.isCompleted && !isNearlyEnd) {
      const seekToSaved = () => {
        if (videoRef.current) {
          videoRef.current.currentTime = watched;
          setProgress(watched);
          setCurrentTimeStr(formatTime(watched));
          setResumeBanner({
            time: watched,
            timeStr: formatTime(watched)
          });
          setTimeout(() => {
            setResumeBanner(null);
          }, 5000);
        }
      };

      if (videoEl.readyState >= 1) {
        seekToSaved();
      } else {
        videoEl.addEventListener('loadedmetadata', seekToSaved, { once: true });
      }
    } else {
      setProgress(0);
      setCurrentTimeStr('0:00');
      setResumeBanner(null);
    }
  }, [selectedVideo?.id, selectedVideo?.videoUrl]);

  // Save progress before page unload or component unmount
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (videoRef.current && selectedVideoRef.current) {
        const cur = videoRef.current.currentTime;
        const dur = videoRef.current.duration;
        const videoId = getVideoNumericId(selectedVideoRef.current);
        if (videoId && cur > 0) {
          const payload = JSON.stringify({
            videoid: Number(videoId) || videoId,
            watched_seconds: Math.floor(cur),
            duration_seconds: Math.floor(dur || 0)
          });
          const token = localStorage.getItem('authToken');
          try {
            fetch(`${baseUrl}${apiUrls.saveprogress}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
              },
              body: payload,
              keepalive: true
            });
          } catch (e) {}
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (videoRef.current && selectedVideoRef.current) {
        const cur = videoRef.current.currentTime;
        const dur = videoRef.current.duration;
        if (cur > 0) {
          sendProgress(cur, dur, true);
        }
      }
    };
  }, [sendProgress]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setIsMuted(val === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
      if (newMuted) setVolume(0);
      else {
        videoRef.current.volume = 1;
        setVolume(1);
      }
    }
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setProgress(time);
      setCurrentTimeStr(formatTime(time));
      sendProgress(time, videoRef.current.duration, true);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      playerContainerRef.current?.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const changePlaybackRate = (rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) videoRef.current.playbackRate = rate;
    setShowSpeedMenu(false);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  const handleMouseLeave = () => {
    if (isPlaying) setShowControls(false);
  };

  const handleVideoSelect = (item) => {
    if (videoRef.current && selectedVideoRef.current) {
      sendProgress(videoRef.current.currentTime, videoRef.current.duration, true);
    }
    setSelectedVideo(item);
    const parentTopic = topics.find(t => t.items?.some(v => String(getVideoNumericId(v)) === String(getVideoNumericId(item))));
    if (parentTopic) {
      setExpandedSyllabus(parentTopic.id);
    }
    setIsPlaying(false);
    setIsVideoEnded(false);
    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTopicClick = (topic) => {
    if (expandedSyllabus === topic.id) {
      setExpandedSyllabus(null);
    } else {
      setExpandedSyllabus(topic.id);
      if (topic.items && topic.items.length > 0) {
        const isCurrentInTopic = topic.items.some(v => String(getVideoNumericId(v)) === String(getVideoNumericId(selectedVideo)));
        if (!isCurrentInTopic) {
          handleVideoSelect(topic.items[0]);
        }
      } else {
        setSelectedVideo(null);
      }
    }
  };

  const handleBackToCourse = () => {
    navigate('/mycourse');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
          <p className="text-slate-500 font-bold animate-pulse">Loading course content...</p>
        </div>
      </div>
    );
  }

  const currentSyllabus = topics.find(t => t.id === expandedSyllabus) || 
    topics.find(t => t.items?.some(v => String(getVideoNumericId(v)) === String(getVideoNumericId(selectedVideo)))) || 
    topics[0];
  const displayCurriculumHeading = currentSyllabus?.title;
  const displayTitle = selectedVideo?.title || currentSyllabus?.title || courseInfo?.coursename || "Course Overview";
  const displayDuration = selectedVideo?.duration || currentSyllabus?.duration || "0:00";
  const displayDescription = selectedVideo?.description || currentSyllabus?.description || "In this module, we dive deep into the fundamental concepts of the course. Stay tuned for expert insights and practical demonstrations.";
  const displayVariants = (selectedVideo?.variants && selectedVideo.variants.length > 0) ? selectedVideo.variants : (currentSyllabus?.variants || []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      <CourseHeader />

      <div className="pt-24 pb-12">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button 
                onClick={handleBackToCourse}
                className="p-2.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:text-orange-600 hover:border-orange-200 transition-all active:scale-95 cursor-pointer shadow-sm"
              >
                <ArrowLeft size={18} />
              </button>
              <nav className="flex items-center gap-2 text-sm font-medium">
                <span className="text-slate-400 hover:text-slate-600 cursor-pointer" onClick={() => navigate('/mycourse')}>My Courses</span>
                <ChevronRight size={14} className="text-slate-300" />
                <span className="text-slate-800 line-clamp-1 max-w-[200px] sm:max-w-none">
                  {courseInfo?.coursename || "Course Details"}
                </span>
              </nav>
            </div>
          </div>

          <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 lg:items-stretch mb-10">
            
            {/* Main Content: Player Area (8 columns) */}
            <div className="lg:col-span-8">
              <div 
                ref={playerContainerRef}
                className="relative group rounded-xl overflow-hidden bg-slate-900 h-full flex flex-col items-center justify-center shadow-lg"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                <div className="aspect-video relative h-full w-full flex items-center justify-center bg-slate-950">
                  {selectedVideo?.videoUrl ? (
                    <>
                      <video
                        ref={videoRef}
                        id="course-video"
                        key={selectedVideo.id}
                        disablePictureInPicture
                        playsInline
                        preload="auto"
                        onPlay={() => {
                          setIsPlaying(true);
                          setIsVideoEnded(false);
                        }}
                        onTimeUpdate={(e) => {
                          const current = e.target.currentTime;
                          const duration = e.target.duration;
                          setProgress(current);
                          setCurrentTimeStr(formatTime(current));
                          sendProgress(current, duration);
                        }}
                        onLoadedMetadata={(e) => {
                          setDurationStr(formatTime(e.target.duration));
                        }}
                        onPause={(e) => {
                          setIsPlaying(false);
                          sendProgress(e.target.currentTime, e.target.duration, true);
                        }}
                        onEnded={() => {
                          setIsPlaying(false);
                          setIsVideoEnded(true);
                          const dur = videoRef.current?.duration || selectedVideo.duration_seconds || 0;
                          sendProgress(dur, dur, true);
                        }}
                        onClick={togglePlay}
                        className="w-full h-full object-contain cursor-pointer"
                        src={selectedVideo.videoUrl}
                      >
                        Your browser does not support the video tag.
                      </video>

                      {/* YouTube-style Resume Notification */}
                      {resumeBanner && (
                        <div className="absolute top-4 left-4 z-40 bg-slate-900/90 backdrop-blur-md text-white px-4 py-2.5 rounded-xl border border-white/10 flex items-center gap-3 shadow-xl">
                          <div className="w-2 h-2 rounded-full bg-orange-500 animate-ping" />
                          <span className="text-xs font-medium">
                            Resumed from <span className="font-bold text-orange-400">{resumeBanner.timeStr}</span>
                          </span>
                          <button
                            onClick={() => {
                              if (videoRef.current) {
                                videoRef.current.currentTime = 0;
                                setProgress(0);
                                setCurrentTimeStr('0:00');
                                setResumeBanner(null);
                                sendProgress(0, videoRef.current.duration, true);
                              }
                            }}
                            className="text-[11px] font-bold bg-white/20 hover:bg-white/30 text-white px-2.5 py-1 rounded-lg transition active:scale-95 cursor-pointer ml-1"
                          >
                            Start Over
                          </button>
                        </div>
                      )}

                      {/* Video Completed Screen */}
                      {isVideoEnded && (
                        <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm z-30 flex flex-col items-center justify-center text-white p-6 text-center">
                          <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-3 shadow-lg">
                            <CheckCircle size={36} />
                          </div>
                          <h3 className="text-lg font-black mb-1">Lesson Completed!</h3>
                          <p className="text-xs text-slate-400 max-w-sm mb-6">
                            You've completed "{selectedVideo.title}".
                          </p>
                          <div className="flex flex-wrap items-center justify-center gap-3">
                            <button
                              onClick={() => handleResetProgress(selectedVideo)}
                              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-xs font-bold transition active:scale-95 cursor-pointer"
                            >
                              <RotateCcw size={14} />
                              Watch Again
                            </button>
                            <button
                              onClick={handleNextVideo}
                              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-bold shadow-lg transition active:scale-95 cursor-pointer"
                            >
                              <span>Next Lesson</span>
                              <ChevronRight size={15} />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Controls Overlay */}
                      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 transition-opacity duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}>
                        {/* Progress Bar (YouTube Style) */}
                        <div className="flex items-center gap-2 mb-2">
                          <input 
                            type="range" 
                            min="0" 
                            max={videoRef.current?.duration || 0} 
                            value={progress || 0} 
                            onChange={handleSeek}
                            className="w-full h-1.5 bg-white/30 rounded-lg cursor-pointer accent-red-600 hover:h-2 transition-all"
                          />
                        </div>
                        
                        <div className="flex items-center justify-between text-white mt-2">
                          <div className="flex items-center gap-4">
                            <button onClick={togglePlay} className="hover:text-orange-400 transition cursor-pointer">
                              {isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" />}
                            </button>
                            
                            <span className="text-xs font-semibold tracking-wider tabular-nums">
                              {currentTimeStr} / {durationStr}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <button onClick={toggleMute} className="hover:text-orange-400 transition cursor-pointer">
                                {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                              </button>
                            </div>

                            <div className="relative">
                              <button 
                                onClick={() => setShowSpeedMenu(!showSpeedMenu)} 
                                className="hover:text-orange-400 transition flex items-center gap-1 cursor-pointer"
                                title="Playback Speed"
                              >
                                <PlaybackSpeedIcon size={20} />
                                {playbackRate !== 1 && <span className="text-xs font-bold">{playbackRate}x</span>}
                              </button>
                              
                              {showSpeedMenu && (
                                <div className="absolute bottom-full right-0 mb-4 bg-slate-900/95 backdrop-blur border border-slate-700 rounded-lg py-2 flex flex-col shadow-xl min-w-[110px] z-50">
                                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                                    <button 
                                      key={rate} 
                                      onClick={() => changePlaybackRate(rate)}
                                      className={`px-4 py-2 text-xs text-left hover:bg-slate-800 transition cursor-pointer ${playbackRate === rate ? 'text-orange-500 font-bold' : 'text-white'}`}
                                    >
                                      {rate === 1 ? 'Normal' : `${rate}x`}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            <button onClick={toggleFullscreen} className="hover:text-orange-400 transition cursor-pointer">
                              <Maximize size={20} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8 text-center bg-slate-900">
                      <div className="w-20 h-20 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center mb-4">
                        <Play size={40} />
                      </div>
                      <h3 className="text-xl font-bold mb-2">
                        {selectedVideo ? "No Video Available" : "Select a Lesson"}
                      </h3>
                      <p className="text-slate-400 text-sm max-w-xs">
                        {selectedVideo ? "This module doesn't have a video associated with it." : "Pick a video from the syllabus on the right to start your learning journey."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Sidebar: Syllabus & Playlist (4 columns) */}
            <div className="lg:col-span-4">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full min-h-[480px]">
                <div className="p-4 px-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
                   <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
                      <BookOpen size={18} className="text-orange-500" />
                      Course Content
                   </h3>
                   <span className="text-[11px] font-bold text-slate-400 bg-white px-2 py-0.5 rounded-md border border-slate-200">
                     {topics.reduce((acc, t) => acc + (t.items ? t.items.length : 0), 0)} Lessons
                   </span>
                </div>
                
                <div className="flex-1 overflow-y-auto no-scrollbar">
                  <div className="divide-y divide-slate-100">
                    {topics.map(topic => (
                      <div key={topic.id} className="flex flex-col">
                        {/* Syllabus Header */}
                        <div
                          className={`group flex items-center gap-3 p-3.5 hover:bg-slate-50 transition-all cursor-pointer ${expandedSyllabus === topic.id ? 'bg-orange-50/30' : ''}`}
                          onClick={() => handleTopicClick(topic)}
                        >
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${expandedSyllabus === topic.id ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-500'}`}>
                            <BookOpen size={16} />
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col">
                            <p className={`text-xs font-bold truncate ${expandedSyllabus === topic.id ? 'text-orange-600' : 'text-slate-800'}`}>
                              {topic.title}
                            </p>
                            {topic.duration && (
                              <div className="flex items-center gap-2 mt-0.5 text-[10px] font-medium text-slate-400">
                                <span className="flex items-center gap-1"><Clock size={10} /> {topic.duration}</span>
                              </div>
                            )}
                          </div>
                          {expandedSyllabus === topic.id ? (
                            <ChevronDown size={15} className="text-orange-500 transition-transform" />
                          ) : (
                            <ChevronRight size={15} className="text-slate-300 group-hover:translate-x-0.5 transition-transform" />
                          )}
                        </div>

                        {/* Video Items (YouTube Playlist Style) */}
                        {expandedSyllabus === topic.id && (
                          topic.items && topic.items.length > 0 ? (
                            <div className="divide-y divide-slate-50 bg-slate-50/40">
                              {topic.items.map(video => {
                                const isSelected = selectedVideo?.id === video.id;
                                const watched = Number(video.watched_seconds) || 0;
                                const dur = Number(video.duration_seconds) || parseDurationToSeconds(video.duration) || 0;
                                const percent = video.progressPercent || (dur > 0 ? Math.round((watched / dur) * 100) : 0);

                                return (
                                  <div 
                                    key={video.id}
                                    className={`group/item relative px-4 py-3 cursor-pointer transition-all border-l-4 ${
                                      isSelected 
                                        ? 'bg-orange-50/80 border-orange-500 shadow-sm' 
                                        : 'hover:bg-slate-100/60 border-transparent'
                                    }`}
                                    onClick={() => handleVideoSelect(video)}
                                  >
                                    <div className="flex items-start gap-3">
                                      {/* Play / Completed Badge */}
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-transform group-hover/item:scale-105 ${
                                        isSelected 
                                          ? 'bg-orange-500 text-white shadow-md' 
                                          : video.isCompleted
                                          ? 'bg-emerald-100 text-emerald-600'
                                          : 'bg-slate-200 text-slate-600 group-hover/item:bg-orange-100 group-hover/item:text-orange-600'
                                      }`}>
                                        {video.isCompleted ? (
                                          <CheckCircle size={15} className="text-emerald-600" />
                                        ) : (
                                          <Play size={12} className="ml-0.5" fill={isSelected ? "currentColor" : "none"} />
                                        )}
                                      </div>

                                      {/* Video Details */}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-1">
                                          <p className={`text-xs font-bold leading-tight line-clamp-2 ${isSelected ? 'text-orange-600' : 'text-slate-700'}`}>
                                            {video.title}
                                          </p>

                                          {/* Reset Progress Button on hover */}
                                          {(watched > 0 || video.isCompleted) && (
                                            <button
                                              onClick={(e) => handleResetProgress(video, e)}
                                              className="opacity-0 group-hover/item:opacity-100 p-1 text-slate-400 hover:text-orange-600 hover:bg-orange-100/50 rounded transition-all shrink-0"
                                              title="Reset Progress (Watch from start)"
                                            >
                                              <RotateCcw size={11} />
                                            </button>
                                          )}
                                        </div>

                                        {/* Watched Time & Duration info with fixed line height and tabular numbers */}
                                        <div className="flex items-center gap-2 mt-1 min-h-[16px] text-[10px] font-medium tabular-nums">
                                          {video.isCompleted ? (
                                            <span className="text-emerald-600 font-bold flex items-center gap-1">
                                              <CheckCircle size={10} /> Completed {dur > 0 ? `(${formatTime(dur)})` : ''}
                                            </span>
                                          ) : watched > 0 ? (
                                            <div className="flex items-center gap-1 text-slate-500">
                                              <Clock size={10} className="text-orange-500" />
                                              <span className="text-orange-600 font-bold">{formatTime(watched)}</span>
                                              <span>/</span>
                                              <span>{formatTime(dur > 0 ? dur : parseDurationToSeconds(video.duration))}</span>
                                              <span className="text-[9px] text-slate-400 font-normal">({Math.round(percent)}%)</span>
                                            </div>
                                          ) : (
                                            <span className="text-slate-400 flex items-center gap-1">
                                              <Clock size={10} />
                                              {video.duration || formatTime(dur) || "0:00"}
                                            </span>
                                          )}
                                        </div>

                                        {/* YouTube-Style Red Progress Bar Track */}
                                        <div className="w-full bg-slate-200/60 h-1 rounded-full overflow-hidden mt-1.5">
                                          {watched > 0 && (
                                            <div 
                                              className={`h-full transition-all duration-300 ease-out ${video.isCompleted ? 'bg-emerald-500' : 'bg-red-600'}`}
                                              style={{ width: `${Math.min(100, Math.max(3, percent))}%` }}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="pl-16 pr-4 py-4 text-xs text-slate-400 italic flex items-center gap-2">
                              <Info size={14} className="text-slate-400" />
                              No video available
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Full Width Overview & Attachments Box */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="border-b border-slate-100 px-6 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex flex-col gap-1">
                  {displayCurriculumHeading && (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-600 bg-orange-50 px-2.5 py-0.5 rounded-md border border-orange-200">
                        <BookOpen size={12} className="text-orange-500" />
                        {displayCurriculumHeading}
                      </span>
                    </div>
                  )}
                  <h1 className="text-base sm:text-lg font-black text-slate-800 leading-tight">
                    {displayTitle}
                  </h1>
                </div>

                <div className="flex items-center gap-3">
                  {/* Watch Again / Reset Progress button */}
                  {(selectedVideo?.watched_seconds > 0 || selectedVideo?.isCompleted) && (
                    <button
                      onClick={() => handleResetProgress(selectedVideo)}
                      className="flex items-center gap-1.5 px-3 py-1 text-xs font-bold text-slate-600 bg-slate-50 hover:bg-orange-50 hover:text-orange-600 rounded-lg border border-slate-200 transition active:scale-95 cursor-pointer"
                      title="Reset watched time and replay from start"
                    >
                      <RotateCcw size={12} />
                      Watch Again
                    </button>
                  )}

                  <div className="flex items-center text-xs font-bold text-slate-600 gap-1.5 px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200">
                    <Clock size={13} className="text-orange-500" />
                    {displayDuration}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="prose prose-slate max-w-none text-xs sm:text-sm">
                <p className="text-slate-600 leading-relaxed">
                  {displayDescription}
                </p>
              </div>

              {displayVariants && displayVariants.length > 0 && (
                <div className="space-y-2 pt-4 mt-4 border-t border-slate-100">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Lesson Attachments</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {displayVariants.map((v, idx) => (
                      <a 
                        key={idx} 
                        href={getAttachmentUrl(v) || '#'}
                        download={v.name || v.fileurl?.split('/').pop() || 'Attachment'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (!getAttachmentUrl(v)) {
                            e.preventDefault();
                            toast.error("Download link not available");
                          }
                        }}
                        className="flex items-center gap-2.5 p-2 bg-slate-50 border border-slate-200 rounded-xl hover:border-orange-200 hover:bg-orange-50/30 transition-all cursor-pointer group max-w-[220px]"
                        title={v.name || v.fileurl?.split('/').pop() || 'Download Resource'}
                      >
                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600 shrink-0 group-hover:scale-105 transition-transform">
                          <Download size={16} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-800 truncate">
                            {(v.name || v.fileurl?.split('/').pop() || 'Resource File').length > 18 
                              ? (v.name || v.fileurl?.split('/').pop() || 'Resource File').substring(0, 18) + '...' 
                              : (v.name || v.fileurl?.split('/').pop() || 'Resource File')}
                          </p>
                          <p className="text-[10px] text-slate-400 font-medium">Attachment</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;